'use client'
import React from 'react';
import styled, { keyframes } from 'styled-components';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useUpdate } from '@/contexts/UpdateContext';

const slideIn = keyframes`
    from { transform: translateY(16px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

const Banner = styled.div`
    position: fixed;
    bottom: 20px;
    left: 20px;
    z-index: 9999;
    background: rgba(8, 10, 15, 0.97);
    border: 1px solid rgba(99, 102, 241, 0.35);
    border-radius: 12px;
    padding: 14px 16px;
    min-width: 300px;
    max-width: 360px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(99, 102, 241, 0.08);
    backdrop-filter: blur(20px);
    animation: ${slideIn} 0.3s ease-out;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TextBlock = styled.div`
    flex: 1;
    min-width: 0;
`;

const Label = styled.div`
    font-family: 'Chakra Petch', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.92);
    letter-spacing: 0.4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Sub = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.38);
    margin-top: 2px;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const Btn = styled.button`
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 11px;
    border-radius: 6px;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid ${({ $variant }) =>
        $variant === 'success' ? 'rgba(34, 197, 94, 0.4)' : 'rgba(99, 102, 241, 0.4)'};
    background: ${({ $variant }) =>
        $variant === 'success' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(99, 102, 241, 0.15)'};
    color: ${({ $variant }) => ($variant === 'success' ? '#86efac' : '#a5b4fc')};

    &:hover {
        background: ${({ $variant }) =>
            $variant === 'success' ? 'rgba(34, 197, 94, 0.25)' : 'rgba(99, 102, 241, 0.3)'};
        color: #fff;
    }
`;

const IconBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 4px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.25);
    padding: 0;
    transition: color 0.2s;
    &:hover { color: rgba(255, 255, 255, 0.75); }
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 3px;
    background: rgba(255, 255, 255, 0.07);
    border-radius: 2px;
    overflow: hidden;
`;

const ProgressFill = styled.div`
    height: 100%;
    background: linear-gradient(90deg, #6366f1, #a855f7);
    border-radius: 2px;
    transition: width 0.4s ease;
    width: ${({ $pct }) => $pct}%;
`;

const UpdateBanner = () => {
    const { phase, version, progress, dismissed, setDismissed, download, install } = useUpdate();

    // El banner solo muestra cuando hay una actualización activa (no en checking/up-to-date)
    const bannerPhases = ['available', 'downloading', 'ready'];
    if (!bannerPhases.includes(phase) || dismissed) return null;

    return (
        <Banner>
            <Row>
                {phase === 'ready' ? (
                    <CheckCircleIcon style={{ fontSize: 18, color: '#86efac', flexShrink: 0 }} />
                ) : (
                    <SystemUpdateAltIcon style={{ fontSize: 18, color: '#a5b4fc', flexShrink: 0 }} />
                )}

                <TextBlock>
                    {phase === 'available' && (
                        <>
                            <Label>Nueva versión {version}</Label>
                            <Sub>Actualización disponible</Sub>
                        </>
                    )}
                    {phase === 'downloading' && (
                        <Label>Descargando... {progress}%</Label>
                    )}
                    {phase === 'ready' && (
                        <>
                            <Label>Lista para instalar</Label>
                            <Sub>Reiniciá la app para aplicar</Sub>
                        </>
                    )}
                </TextBlock>

                <Actions>
                    {phase === 'available' && (
                        <>
                            <Btn onClick={download}>
                                <DownloadIcon style={{ fontSize: 13 }} />
                                Descargar
                            </Btn>
                            <IconBtn onClick={() => setDismissed(true)} title="Cerrar">
                                <CloseIcon style={{ fontSize: 14 }} />
                            </IconBtn>
                        </>
                    )}
                    {phase === 'ready' && (
                        <>
                            <Btn $variant="success" onClick={install}>
                                <RestartAltIcon style={{ fontSize: 13 }} />
                                Reiniciar
                            </Btn>
                            <IconBtn onClick={() => setDismissed(true)} title="Cerrar">
                                <CloseIcon style={{ fontSize: 14 }} />
                            </IconBtn>
                        </>
                    )}
                </Actions>
            </Row>

            {phase === 'downloading' && (
                <ProgressTrack>
                    <ProgressFill $pct={progress} />
                </ProgressTrack>
            )}
        </Banner>
    );
};

export default UpdateBanner;
